import { Link, useParams, Navigate } from "react-router-dom";
import SEO, { courseSchema, faqSchema, orgSchema, programListSchema, breadcrumbSchema, videoSchema } from "../../lib/seo";
import { PROGRAMS, programBySlug } from "../../data/programs";
import { LOCATIONS, MEDIA, hgLink, trackOutbound, HG_TRIAL_PATH } from "../../data/site";
import { REVIEWS } from "../../data/content";
import { Reveal, Eyebrow, TrialButton, GhostButton, FAQBlock, SectionShell, VideoPlayer } from "../../components/shared";
import { ScheduleFilterable, MembershipCards, ReviewQuoteStrip } from "../../components/interactive";
import { PrivateLessonsSection } from "./misc";
import { Star } from "lucide-react";

/* Verified media per program — see /app/docs/media-manifest.md for sources & holds */
const PROGRAM_MEDIA = {
  "muay-thai-kickboxing": {
    video: {
      ...MEDIA.videos.raenaKickboxing,
      portrait: true,
      title: "Kickboxing training at Mezger Martial Arts, Hidden Gym Richardson",
      caption: "Kickboxing training · Hidden Gym Richardson",
      schemaName: "Kickboxing Training at Mezger Martial Arts — Hidden Gym Richardson",
      schemaDesc: "Kickboxing pad and bag training footage from the Mezger Martial Arts program at Hidden Gym Richardson.",
    },
    photos: [{ src: MEDIA.photos.kickboxingMain, alt: "Wall-pad kick training at Mezger Martial Arts", caption: "Wall-pad training · Mezger Martial Arts", portrait: true }],
  },
  "brazilian-jiu-jitsu": {
    photos: [
      { src: MEDIA.photos.bjjClass, alt: "Brazilian Jiu-Jitsu class lined up on the mats at Mezger Martial Arts", caption: "BJJ class · Mezger Martial Arts" },
      { src: MEDIA.photos.bjjWomen, alt: "Gi throw during Brazilian Jiu-Jitsu training at Mezger Martial Arts", caption: "Live training · Mezger Martial Arts", portrait: true },
      { src: MEDIA.photos.bjjMain, alt: "No-gi Brazilian Jiu-Jitsu rolling at Mezger Martial Arts", caption: "No-gi rolling · Mezger Martial Arts", portrait: true },
    ],
  },
  mma: {
    video: {
      ...MEDIA.videos.mmaAd,
      title: "MMA program overview — Mezger Martial Arts at Hidden Gym",
      caption: "Program overview · Mezger Martial Arts, Richardson",
      schemaName: "MMA Program Overview — Mezger Martial Arts at Hidden Gym",
      schemaDesc: "Short overview of MMA and combat sports training in the Mezger Martial Arts program at Hidden Gym, filmed at the Richardson facility.",
    },
    photos: [
      { src: MEDIA.photos.richardsonMats, alt: "Mats and cage room at Hidden Gym Richardson", caption: "Mats & cage · Richardson" },
      { src: MEDIA.photos.northDallasMats, alt: "Martial arts mat room at Hidden Gym North Dallas, 2026", caption: "Mat room · North Dallas (2026)" },
      { src: MEDIA.photos.northDallasBags, alt: "Mezger Martial Arts heavy bags at Hidden Gym North Dallas", caption: "Heavy bags · North Dallas" },
    ],
  },
  boxing: {
    photos: [
      { src: MEDIA.photos.boxingMain, alt: "Boxing pad work at Mezger Martial Arts", caption: "Pad work · Mezger Martial Arts", portrait: true },
      { src: MEDIA.photos.boxingNorthDallas, alt: "Boxing pad coaching at Hidden Gym North Dallas, July 2026", caption: "Pad coaching · North Dallas (2026)", portrait: true },
    ],
  },
  wrestling: {
    photos: [{ src: MEDIA.photos.wrestlingMain, alt: "Grappling class training on the mats at Mezger Martial Arts", caption: "On the mats · Mezger Martial Arts" }],
  },
  "kids-martial-arts": {
    photos: [{ src: MEDIA.photos.kidsCombat, alt: "Kids Combat Sports class on the mats at Mezger Martial Arts", caption: "Kids Combat Sports · Mezger Martial Arts" }],
  },
  "lion-cubs": {
    photos: [{ src: MEDIA.photos.lionCubsPhoto, alt: "Lion Cubs class group photo at Mezger Martial Arts", caption: "Lion Cubs · Mezger Martial Arts" }],
  },
  "private-lessons": {
    photos: [
      { src: MEDIA.photos.privateLessonsPhoto, alt: "One-on-one pad coaching at Mezger Martial Arts", caption: "One-on-one coaching · Mezger Martial Arts" },
      { src: MEDIA.photos.boxingRichardson, alt: "Coach holding pads for a student at Hidden Gym Richardson", caption: "Coaching · Richardson", portrait: true },
    ],
  },
};

const ProgramMediaSection = ({ slug, programName }) => {
  const media = PROGRAM_MEDIA[slug];
  if (!media) return null;
  return (
    <SectionShell dark className="grain">
      <Reveal>
        <Eyebrow>Inside the Room</Eyebrow>
        <h2 className="mt-2 font-display font-black uppercase text-2xl sm:text-3xl lg:text-4xl">{programName.split(" (")[0]}<br /><span className="text-neutral-400">at Hidden Gym.</span></h2>
      </Reveal>
      <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6 items-start" data-testid={`program-media-${slug}`}>
        {media.video && (
          <Reveal className={media.video.portrait ? "" : "md:col-span-2"}>
            <VideoPlayer
              src={media.video.src}
              poster={media.video.poster}
              title={media.video.title}
              caption={media.video.caption}
              portrait={media.video.portrait}
              dark
              testId={`program-video-${slug}`}
            />
          </Reveal>
        )}
        {media.photos?.map((ph, i) => (
          <Reveal key={ph.src} delay={i * 0.08}>
            <figure>
              <img src={ph.src} alt={ph.alt} loading="lazy" className={`w-full object-cover border border-white/10 ${ph.portrait ? "aspect-[3/4]" : "aspect-video"}`} />
              <figcaption className="mt-2 font-mono text-[11px] uppercase tracking-widest text-white/50">{ph.caption}</figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
    </SectionShell>
  );
};

const programVideoSchemas = (slug) => {
  const v = PROGRAM_MEDIA[slug]?.video;
  if (!v) return [];
  return [videoSchema({ name: v.schemaName, description: v.schemaDesc, path: v.src, poster: v.poster, uploadDate: "2026-09-15", duration: v.duration })];
};

export const ProgramsIndex = () => (
  <>
    <SEO
      title="Martial Arts Programs in Richardson & North Dallas | Mezger Martial Arts"
      description="Muay Thai, Boxing, Brazilian Jiu-Jitsu, Wrestling, MMA, Kids programs, and private lessons at Hidden Gym Richardson & North Dallas. All levels, ages 5+. First class free."
      schemas={[orgSchema(), programListSchema(PROGRAMS), videoSchema({ name: "Mezger Martial Arts Program Overview", description: "Short overview of the Mezger Martial Arts program at Hidden Gym — Muay Thai, boxing, BJJ, wrestling, and MMA training filmed at the Richardson facility.", path: MEDIA.videos.mmaAd.src, poster: MEDIA.videos.mmaAd.poster, uploadDate: "2026-09-15", duration: MEDIA.videos.mmaAd.duration }), breadcrumbSchema([["Home", "/"], ["Programs", "/programs"]])]}
    />
    <section className="bg-[#0A0B0D] text-white pt-24 pb-16 grain relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Eyebrow>Programs</Eyebrow>
          <h1 className="mt-3 font-display font-black uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-none">Eight Ways In.<br /><span className="text-neutral-400">One Standard.</span></h1>
          <p className="mt-4 max-w-2xl text-white/70">Every program below runs inside Hidden Gym, is beginner-friendly unless marked otherwise, and starts the same way: a free trial class.</p>
        </Reveal>
        <div className="mt-10 grid lg:grid-cols-2 gap-8 items-start">
          <Reveal>
            <VideoPlayer
              src={MEDIA.videos.mmaAd.src}
              poster={MEDIA.videos.mmaAd.poster}
              title="Mezger Martial Arts program overview"
              caption="Program overview · Filmed at Hidden Gym Richardson"
              dark
              testId="programs-overview-video"
            />
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-6">
            {[
              { slug: "richardson", img: MEDIA.photos.richardsonMats, label: "Richardson", sub: "235 N Central Expy · Mats & cage room" },
              { slug: "north-dallas", img: MEDIA.photos.northDallasMats, label: "North Dallas", sub: "17390 Preston Rd · 2026 mat room" },
            ].map((f, i) => (
              <Reveal key={f.slug} delay={i * 0.08}>
                <Link to={`/locations/${f.slug}`} className="group block border border-white/15 hover:border-white transition-colors" data-testid={`programs-facility-card-${f.slug}`}>
                  <img src={f.img} alt={`Martial arts training space at Hidden Gym ${f.label}`} loading="lazy" className="aspect-video w-full object-cover group-hover:opacity-85 transition-opacity" />
                  <div className="p-4">
                    <h2 className="font-display font-black uppercase text-lg">{f.label}</h2>
                    <p className="mt-1 font-mono text-[11px] uppercase tracking-wider text-white/50">{f.sub}</p>
                    <p className="mt-2 text-sm underline underline-offset-4 text-white/75 group-hover:text-white">Location details →</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
    <SectionShell>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {PROGRAMS.map((p, i) => (
          <Reveal key={p.slug} delay={(i % 3) * 0.08} className="h-full">
            <div className="group h-full flex flex-col bg-white border border-black/10 hover:border-[#0A0B0D] transition-colors overflow-hidden" data-testid={`programs-index-card-${p.slug}`}>
              <Link to={`/programs/${p.slug}`} className="aspect-[4/3] overflow-hidden block">
                <img src={p.image} alt={p.imageAlt} loading="lazy" className={`w-full h-full object-cover ${p.imagePos || "object-center"} group-hover:scale-105 transition-transform duration-500`} />
              </Link>
              <div className="p-6 flex flex-col flex-1">
                <h2 className="font-display font-black uppercase text-2xl leading-tight"><Link to={`/programs/${p.slug}`} className="hover:text-neutral-500">{p.name}</Link></h2>
                <p className="mt-1 font-mono text-[11px] uppercase tracking-wider text-[#5A6270]">{p.level} · Ages {p.ages} · {p.locations.map((l) => LOCATIONS.find((x) => x.slug === l)?.short).join(" & ")}</p>
                <p className="mt-3 text-sm text-[#374151] flex-1">{p.card}</p>
                <div className="mt-4 flex gap-2">
                  <Link to={`/programs/${p.slug}`} className="flex-1 text-center border border-[#0A0B0D] font-display font-bold uppercase tracking-wide py-2.5 hover:bg-[#0A0B0D] hover:text-white transition-colors text-sm">Details</Link>
                  <Link to={hgLink(HG_TRIAL_PATH, `programs-${p.slug}`)} onClick={() => trackOutbound("programs", p.slug)} className="flex-1 text-center bg-[#111315] border border-white/20 hover:bg-neutral-200 hover:text-[#0A0B0D] text-white font-display font-bold uppercase tracking-wide py-2.5 transition-colors text-sm">Free Trial</Link>
                </div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </SectionShell>
  </>
);

export default function ProgramPage() {
  const { slug } = useParams();
  const p = programBySlug(slug);
  if (!p) return <Navigate to="/programs" replace />;
  const locs = p.locations.map((l) => LOCATIONS.find((x) => x.slug === l));
  const related = PROGRAMS.filter((x) => x.slug !== slug).slice(0, 3);
  const reviews = REVIEWS.filter((r) => p.name.toLowerCase().includes(r.program.toLowerCase()) || r.program.toLowerCase().includes(p.name.split(" ")[0].toLowerCase())).slice(0, 3);

  return (
    <>
      <SEO title={p.meta.title} description={p.meta.description} schemas={[courseSchema(p), faqSchema(p.faqs), ...programVideoSchemas(p.slug), breadcrumbSchema([["Home", "/"], ["Programs", "/programs"], [p.name, `/programs/${p.slug}`]])]} />
      <section className="relative bg-[#0A0B0D] text-white pt-24 pb-16 overflow-hidden grain">
        <img src={p.image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B0D] to-transparent" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Eyebrow>{locs.map((l) => l.short).join(" · ")} · {p.level} · Ages {p.ages}</Eyebrow>
            <h1 className="mt-3 font-display font-black uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-none max-w-3xl">{p.name} <span className="text-neutral-400">in Richardson & North Dallas</span></h1>
            <div className="mt-8 flex flex-wrap gap-4">
              <TrialButton campaign={`program-${p.slug}`} page={`program-${p.slug}`} testId="program-hero-trial-btn" />
              <GhostButton to="/schedule" className="text-white border-white/40" testId="program-hero-schedule-btn">See Schedule</GhostButton>
            </div>
          </Reveal>
        </div>
      </section>

      <ReviewQuoteStrip programSlug={p.slug} />

      <ProgramMediaSection slug={p.slug} programName={p.name} />

      <SectionShell className="bg-white">
        <div className="grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-10">
            <Reveal>
              <h2 className="font-display font-black uppercase text-2xl sm:text-3xl">What is {p.name.split(" (")[0]}?</h2>
              <p className="mt-3 text-[#374151] leading-relaxed text-lg">{p.what}</p>
            </Reveal>
            <Reveal>
              <h2 className="font-display font-black uppercase text-2xl sm:text-3xl">Who It's For</h2>
              <p className="mt-3 text-[#374151] leading-relaxed">{p.whoFor}</p>
            </Reveal>
            <Reveal>
              <h2 className="font-display font-black uppercase text-2xl sm:text-3xl">How It Compares</h2>
              <p className="mt-3 text-[#374151] leading-relaxed">{p.compare}</p>
            </Reveal>
            <Reveal>
              <h2 className="font-display font-black uppercase text-2xl sm:text-3xl">Fitness & Self-Defense Benefits</h2>
              <ul className="mt-4 grid sm:grid-cols-2 gap-3">
                {p.benefits.map((b) => (
                  <li key={b} className="flex gap-3 items-start bg-[#F7F8F9] border border-black/5 p-4"><span className="text-neutral-400 font-black">■</span><span className="text-sm text-[#374151]">{b}</span></li>
                ))}
              </ul>
            </Reveal>
          </div>
          <aside className="space-y-6">
            <Reveal>
              <div className="bg-[#0A0B0D] text-white p-6">
                <h3 className="font-display font-black uppercase text-xl">First Class Free</h3>
                <p className="mt-2 text-white/70 text-sm">No experience needed. Gloves and pads provided. No sparring on day one, ever.</p>
                <TrialButton campaign={`program-side-${p.slug}`} page={`program-${p.slug}`} testId="program-sidebar-trial-btn" className="mt-4 w-full" />
              </div>
            </Reveal>
            <Reveal>
              <div className="border border-black/10 p-6">
                <h3 className="font-display font-black uppercase text-lg">Offered At</h3>
                {locs.map((l) => (
                  <div key={l.slug} className="mt-3 text-sm">
                    <Link to={`/locations/${l.slug}`} className="font-bold hover:text-neutral-500">{l.name}</Link>
                    <p className="text-[#5A6270]">{l.address}</p>
                  </div>
                ))}
              </div>
            </Reveal>
            <Reveal>
              <div className="border border-black/10 p-6 text-sm space-y-2">
                <h3 className="font-display font-black uppercase text-lg">Keep Exploring</h3>
                <p><Link to="/schedule" className="underline hover:text-neutral-500">Full class schedule</Link></p>
                <p><Link to="/membership" className="underline hover:text-neutral-500">Membership & pricing (via Hidden Gym)</Link></p>
                <p><Link to="/about" className="underline hover:text-neutral-500">About head instructor Guy Mezger</Link></p>
                <p><Link to="/programs/private-lessons" className="underline hover:text-neutral-500">Private lessons</Link></p>
                {p.slug !== "kids-martial-arts" && <p><Link to="/programs/kids-martial-arts" className="underline hover:text-neutral-500">Kids version (ages 7+)</Link></p>}
                {related.map((r) => <p key={r.slug}><Link to={`/programs/${r.slug}`} className="underline hover:text-neutral-500">{r.name}</Link></p>)}
              </div>
            </Reveal>
          </aside>
        </div>
      </SectionShell>

      {p.slug === "private-lessons" ? (
        <PrivateLessonsSection />
      ) : (
        <SectionShell>
          <Reveal>
            <Eyebrow>Schedule</Eyebrow>
            <h2 className="mt-2 font-display font-black uppercase text-2xl sm:text-3xl lg:text-4xl">{p.name.split(" (")[0]} Classes This Week</h2>
          </Reveal>
          <div className="mt-8"><ScheduleFilterable lockProgram={p.slug} compact /></div>
        </SectionShell>
      )}

      {reviews.length > 0 && (
        <SectionShell className="bg-white">
          <Reveal><Eyebrow>What Students Say</Eyebrow></Reveal>
          <div className="mt-6 grid md:grid-cols-3 gap-6">
            {reviews.map((r, ri) => (
              <figure key={ri} className="border border-black/10 p-6">
                <div className="flex text-[#F59E0B]">{Array.from({ length: r.stars }).map((_, s) => <Star key={s} size={14} fill="currentColor" />)}</div>
                <blockquote className="mt-3 text-sm text-[#374151]">"{r.text}"</blockquote>
                <figcaption className="mt-3 text-xs font-bold">{r.program} · <span className="font-normal text-[#5A6270]">{r.source}</span></figcaption>
              </figure>
            ))}
          </div>
        </SectionShell>
      )}

      <SectionShell>
        <Reveal>
          <Eyebrow>Membership</Eyebrow>
          <h2 className="mt-2 font-display font-black uppercase text-2xl sm:text-3xl lg:text-4xl">Ready to Train? Sign Up Through Hidden Gym.</h2>
        </Reveal>
        <div className="mt-10"><MembershipCards /></div>
      </SectionShell>

      <SectionShell className="bg-white">
        <Reveal>
          <Eyebrow>FAQ</Eyebrow>
          <h2 className="mt-2 font-display font-black uppercase text-2xl sm:text-3xl lg:text-4xl">{p.name.split(" (")[0]} Questions</h2>
        </Reveal>
        <div className="mt-6 max-w-3xl"><FAQBlock faqs={p.faqs} testPrefix={`faq-${p.slug}`} /></div>
      </SectionShell>
    </>
  );
}
